import { range, scaleOrdinal, schemeCategory10, min, max, scaleLinear, scaleSqrt, scaleLog, descending, select } from 'd3';
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { ResizeComponent } from '@iva/resize-component';
import { cloud } from '@inode/d3-cloud';
import clonedeep from 'lodash.clonedeep';
import seedrandom from 'seedrandom';
import { Paper, Typography } from '@mui/material';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * Selects an item from a given array, the index of the selected item is generated by the given random number generator
 */
function choose(array, random) {
    return array[Math.floor(random() * array.length)];
}
/**
 * Generates a default set of colors
 */
function getDefaultColors() {
    return range(20)
        .map(number => number.toString())
        .map(scaleOrdinal(schemeCategory10));
}
/**
 * Generates a D3 scale object for the given configuration
 */
function getFontScale(words, fontSizes, scale) {
    const minCount = min(words, word => Number(word.value));
    const maxCount = max(words, word => Number(word.value));
    const domain = [minCount || 0, maxCount || 10];
    let scaleFunction;
    switch (scale) {
        case "log":
            scaleFunction = scaleLog;
            break;
        case "sqrt":
            scaleFunction = scaleSqrt;
            break;
        case "linear":
        default:
            scaleFunction = scaleLinear;
            break;
    }
    const fontScale = scaleFunction().domain(domain).range(fontSizes);
    return fontScale;
}
/**
 * Returns the font size of the given word as string
 */
function getFontSize(word) {
    return `${word.size}px`;
}
/**
 * Returns the text of the given word
 */
function getText(word) {
    return word.text;
}
/**
 * Returns the transform of the given word as string
 */
function getTransform(word) {
    const translate = `translate(${word.x}, ${word.y})`;
    const rotate = typeof word.rotate === "number" ? ` rotate(${word.rotate})` : "";
    return translate + rotate;
}
/**
 * computes a random rotation angle for the given configuration
 */
function rotate(rotations, rotationAngles, random) {
    if (rotations < 1) {
        return 0;
    }
    let angles = [];
    if (rotations === 1) {
        angles = [rotationAngles[0]];
    }
    else {
        angles = [...rotationAngles];
        const increment = (rotationAngles[1] - rotationAngles[0]) / (rotations - 1);
        let angle = rotationAngles[0] + increment;
        while (angle < rotationAngles[1]) {
            angles.push(angle);
            angle += increment;
        }
    }
    return choose(angles, random);
}
function isWord(obj) {
    return (obj.text !== undefined &&
        typeof obj.text === "string" &&
        obj.value !== undefined &&
        typeof obj.value === "number");
}

/**
 * Computes a reasonable Layout for the WordCloud
 * @param param0 options and input
 */
function layout({ callbacks, options, maxWords, size, words, layoutStateUpdate, }) {
    const MAX_LAYOUT_ATTEMPTS = options.maxLayoutAttempts || 10;
    const SHRINK_FACTOR = options.shrinkFactor || 0.95;
    // Const WORD_REMOVAL_FRACTION = options.wordRemovalFraction || 0.1;
    const { deterministic, fontFamily, fontStyle, fontSizes, fontWeight, padding, randomSeed, rotations, rotationAngles, spiral, scale, } = options;
    // sort and slice word array (slice to maxWords)
    const sortedWords = words
        .concat()
        .sort((x, y) => descending(x.value, y.value))
        .slice(0, maxWords);
    // initialize rng
    const random = deterministic
        ? seedrandom(randomSeed || "deterministic")
        : seedrandom();
    // create cloud object
    const cloud$1 = cloud();
    // setup cloud
    cloud$1
        .size(size)
        .padding(padding)
        .words(clonedeep(sortedWords))
        .rotate(() => {
        if (rotations === undefined) {
            // Default rotation algorithm
            return (~~(random() * 6) - 3) * 30;
        }
        return rotate(rotations, rotationAngles, random);
    })
        .spiral(spiral)
        .random(random)
        .text(getText)
        .font(fontFamily)
        .fontStyle(fontStyle)
        .fontWeight(fontWeight);
    // recursively compute layout and decrease size of the cloud until it fits within the available space
    function draw(recursiveFontSizes, attempts = 1) {
        cloud$1
            .fontSize((word) => {
            // compute scale of font sizes
            const fontScale = getFontScale(sortedWords, recursiveFontSizes, scale);
            return fontScale(word.value) || word.value;
        })
            .on("end", (computedWords, bounds) => {
            /** KNOWN ISSUE: https://github.com/jasondavies/d3-cloud/issues/36
             * Recursively layout and decrease font-sizes by a SHRINK_FACTOR.
             * Bail out with a warning message after MAX_LAYOUT_ATTEMPTS.
             */
            if (sortedWords.length !== computedWords.length &&
                attempts <= MAX_LAYOUT_ATTEMPTS) {
                if (attempts === MAX_LAYOUT_ATTEMPTS) {
                    console.warn(`Unable to layout ${sortedWords.length - computedWords.length} word(s) after ${attempts} attempts.  Consider: (1) Increasing the container/component size. (2) Lowering the max font size. (3) Limiting the rotation angles.`);
                }
                const minFontSize = Math.max(recursiveFontSizes[0] * SHRINK_FACTOR, 1);
                const maxFontSize = Math.max(recursiveFontSizes[1] * SHRINK_FACTOR, minFontSize);
                draw([minFontSize, maxFontSize], attempts + 1);
            }
            else {
                if (callbacks.onEnd &&
                    typeof callbacks.onEnd === "function") {
                    callbacks.onEnd(computedWords, bounds);
                }
                layoutStateUpdate({
                    words: computedWords,
                    random: random,
                });
            }
        })
            .start();
    }
    draw(fontSizes);
}
/**
 * Renders words with their appropriate attributes and positions into the SVG
 * @param param0 options and input
 */
function render({ callbacks, options, random, svg, words, ttUpdates, }) {
    // deconstruct callbacks
    const { getWordColor, onWordClick, onWordMouseOver, onWordMouseOut } = callbacks;
    // deconstruct options
    const { colors, enableTooltip, fontFamily, fontStyle, fontWeight, textAttributes, transitionDuration, } = options;
    /**
     * Callbacks
     */
    // returns the fill color for the given word
    function getFill(word) {
        return getWordColor
            ? getWordColor(word)
            : choose(colors, random);
    }
    function onClick(event, word) {
        if (onWordClick) {
            onWordClick(word, event);
        }
    }
    function onMouseOver(event, word) {
        if (enableTooltip && ttUpdates) {
            ttUpdates.updateData(word);
        }
        if (onWordMouseOver) {
            onWordMouseOver(word, event);
        }
    }
    function onMouseOut(event, word) {
        if (enableTooltip && ttUpdates) {
            ttUpdates.updateData(undefined);
        }
        if (onWordMouseOut) {
            onWordMouseOut(word, event);
        }
    }
    function onMouseMove(event) {
        if (enableTooltip && ttUpdates) {
            ttUpdates.updatePos([event.pageX, event.pageY]);
        }
    }
    /**
     * render SVG
     */
    // Load words
    const vizWords = svg
        .select("g")
        .selectAll("text")
        .data(words, (d) => {
        if (isWord(d)) {
            return d.text;
        }
        else {
            return "";
        }
    });
    // enter new text
    let enteredText = vizWords
        .enter()
        .append("text")
        .attr("text-anchor", "middle")
        .attr("font-size", 0)
        .attr("transform", "translate(0, 0) rotate(0)")
        .text(getText);
    // add optional attributes to new text
    if (typeof textAttributes === "object") {
        Object.keys(textAttributes).forEach(key => {
            enteredText = enteredText.attr(key, (word) => {
                const att = textAttributes[key];
                return typeof att === "string" ? att : att(word);
            });
        });
    }
    // set initial attributes
    enteredText
        .attr("font-style", fontStyle)
        .attr("font-weight", fontWeight)
        .attr("font-family", fontFamily)
        .attr("fill", getFill)
        .attr("cursor", onWordClick ? "pointer" : "default")
        .on("click", onClick)
        .on("mouseenter", onMouseOver)
        .on("mouseout", onMouseOut)
        .on("mousemove", onMouseMove);
    // transition text to initial position
    if (transitionDuration > 0) {
        enteredText
            .transition()
            .duration(transitionDuration)
            .attr("font-size", getFontSize)
            .attr("transform", getTransform);
    }
    else {
        enteredText
            .attr("font-size", getFontSize)
            .attr("transform", getTransform);
    }
    // update text attributes
    vizWords
        .attr("font-style", fontStyle)
        .attr("font-weight", fontWeight)
        .attr("font-family", fontFamily)
        .on("click", onClick)
        .on("mouseenter", onMouseOver)
        .on("mouseout", onMouseOut)
        .on("mousemove", onMouseMove)
        .attr("cursor", onWordClick ? "pointer" : "default");
    // update text position and color
    if (transitionDuration > 0) {
        vizWords
            .transition()
            .duration(transitionDuration)
            .attr("fill", getFill)
            .attr("font-size", getFontSize)
            .attr("transform", getTransform)
            .text(getText);
    }
    else {
        vizWords
            .attr("fill", getFill)
            .attr("font-size", getFontSize)
            .attr("transform", getTransform)
            .text(getText);
    }
    // remove text
    if (transitionDuration > 0) {
        vizWords
            .exit()
            .transition()
            .duration(transitionDuration)
            .attr("fill-opacity", 0)
            .attr("font-size", 0)
            .attr("transform", "translate(0, 0) rotate(0)")
            .remove();
    }
    else {
        vizWords.exit().remove();
    }
}

/**
 * Tooltip component
 */
const Tooltip = props => {
    if (!props.shown || props.data === undefined)
        return null;
    return (React.createElement("div", { style: {
            position: "absolute",
            willChange: "transform",
            top: 5,
            left: 5,
            transform: `translate3d(${props.x}px, ${props.y}px, 0px)`,
        } },
        React.createElement(Paper, { sx: { padding: 25, margin: 5 } },
            React.createElement(Typography, null, props.data ? props.callback(props.data) : ""))));
};

/**
 * Constants
 */
const defaultCallbacks = {
    getWordTooltip: ({ text, value }) => `${text} (${value})`,
};
const defaultOptions = {
    colors: getDefaultColors(),
    deterministic: false,
    enableTooltip: true,
    fontFamily: "times new roman",
    fontSizes: [4, 32],
    fontStyle: "normal",
    fontWeight: "normal",
    padding: 1,
    rotationAngles: [-90, 90],
    scale: "sqrt",
    spiral: "rectangular",
    transitionDuration: 600,
    svgAttributes: {},
    textAttributes: {},
};
/**
 * Wordcloud Component with fixed size
 */
const WordCloudFixed = (_a) => {
    var { callbacks = defaultCallbacks, options = defaultOptions, maxWords = 100, fixedSize = [300, 300], words } = _a, rest = __rest(_a, ["callbacks", "options", "maxWords", "fixedSize", "words"]);
    /**
     * The useRef Hook creates a variable that "holds on" to a value across rendering
     * passes. In this case it will hold our component's SVG DOM element. It's
     * initialized null and React will assign it later (see the return statement)
     */
    const d3Container = useRef(null);
    // tooltip state
    const [ttPosition, setTtPosition] = useState([0, 0]);
    const [ttTarget, setTtTarget] = useState(undefined);
    // merge callbacks and options with their default values to get fully defined objects
    const mergedCallbacks = useMemo(() => {
        return Object.assign(Object.assign({}, defaultCallbacks), callbacks);
    }, [callbacks]);
    const mergedOptions = useMemo(() => {
        return Object.assign(Object.assign({}, defaultOptions), options);
    }, [options]);
    // workaround to avoid rerenders caused from resize component
    const internalSizeX = fixedSize[0];
    const internalSizeY = fixedSize[1];
    const internalSize = useMemo(() => {
        return [internalSizeX, internalSizeY];
    }, [internalSizeX, internalSizeY]);
    /**
     * Sets the svg size and group translation
     */
    function updateSize(size) {
        if (d3Container.current) {
            const svg = select(d3Container.current);
            const svgChart = svg.select("g");
            svg.attr("height", size[1]).attr("width", size[0]);
            svgChart.attr("transform", `translate(${size[0] / 2}, ${size[1] / 2})`);
        }
    }
    /**
     * This function is used as layoutEndCallback. We will rerender here.
     */
    const renderCloud = useCallback((currentLayout) => {
        if (d3Container.current &&
            currentLayout.words.length &&
            currentLayout.random) {
            const svg = select(d3Container.current);
            render({
                callbacks: mergedCallbacks,
                options: mergedOptions,
                random: currentLayout.random,
                svg: svg,
                words: currentLayout.words,
                ttUpdates: {
                    updatePos: setTtPosition,
                    updateData: setTtTarget,
                },
            });
        }
    }, [
        d3Container,
        mergedCallbacks,
        mergedOptions,
        setTtPosition,
        setTtTarget,
    ]);
    /**
     * This useEffect block is run only once after the component mounts. We will do the initial D3 setup here.
     */
    useEffect(() => {
        if (d3Container.current) {
            let svg = select(d3Container.current).style("display", "block"); // Native inline svg leaves undesired white space
            // add optional svg attributes
            const svgAttributes = options.svgAttributes;
            if (typeof svgAttributes === "object") {
                Object.keys(svgAttributes).forEach(key => {
                    const att = svgAttributes[key];
                    if (typeof att === "string") {
                        svg = svg.attr(key, att);
                    }
                });
            }
            // add drawing area group
            svg.append("g");
        }
    }, [
        options.svgAttributes,
    ]); /* The empty dependency array of useEffect. This block will run only once after mount. */
    /**
     * This useEffect block is run every time the data, options or component size change. We will do word cloud layout here.
     */
    useEffect(() => {
        if (d3Container.current) {
            // update svg size
            updateSize(internalSize);
            // update layout
            layout({
                callbacks: mergedCallbacks,
                options: mergedOptions,
                maxWords: maxWords,
                size: internalSize,
                words: words,
                layoutStateUpdate: renderCloud,
            });
        }
    }, [
        internalSize,
        words,
        maxWords,
        mergedOptions,
        mergedCallbacks,
        renderCloud,
    ]);
    return (React.createElement("div", Object.assign({}, rest),
        React.createElement("svg", { ref: d3Container }),
        React.createElement(Tooltip, { shown: true, x: ttPosition[0], y: ttPosition[1], data: ttTarget, callback: mergedCallbacks.getWordTooltip })));
};
/**
 * Internal Wordcloud component, computes size from resize component output and minSize.
 * If provided, uses fixedSize instead. Passes arguments to WordCloudFixed.
 */
const WordCloudInternal = (_a) => {
    var { minSize, fixedSize, size = { width: 300, height: 300 } } = _a, rest = __rest(_a, ["minSize", "fixedSize", "size"]);
    return (React.createElement(WordCloudFixed, Object.assign({ fixedSize: fixedSize || [
            minSize ? Math.max(size.width, minSize[0]) : size.width,
            minSize ? Math.max(size.height, minSize[1]) : size.height,
        ] }, rest)));
};
const WordCloudInterface = ResizeComponent(WordCloudInternal);
/**
 * Wordcloud component with responsive size adjustment
 */
const WordCloudResponsive = (_a) => {
    var { minSize, maxHeight = "100%" } = _a, rest = __rest(_a, ["minSize", "maxHeight"]);
    return (React.createElement(WordCloudInterface, Object.assign({ size: {
            height: maxHeight,
        }, minSize: minSize }, rest)));
};

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
class Tokenizer {
    trim(array) {
        while (array[array.length - 1] === "") {
            array.pop();
        }
        while (array[0] === "") {
            array.shift();
        }
        return array;
    }
    tokenize(text) {
        return Array.of(text);
    }
}

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
class AggressiveTokenizer extends Tokenizer {
    constructor() {
        super();
    }
    tokenize(text) {
        return this.trim(text.split(/[\W|_]+/));
    }
}
AggressiveTokenizer.prototype.tokenize = function (text) {
    // break a string up into an array of tokens by anything non-word
    // underscore is considered to be non word character
    return this.trim(text.split(/[\W|_]+/));
};

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
// a list of commonly used words that have little meaning and can be excluded
// from analysis.
const words = [
    "about",
    "above",
    "after",
    "again",
    "all",
    "also",
    "am",
    "an",
    "and",
    "another",
    "any",
    "are",
    "as",
    "at",
    "be",
    "because",
    "been",
    "before",
    "being",
    "below",
    "between",
    "both",
    "but",
    "by",
    "came",
    "can",
    "cannot",
    "come",
    "could",
    "did",
    "do",
    "does",
    "doing",
    "during",
    "each",
    "few",
    "for",
    "from",
    "further",
    "get",
    "got",
    "has",
    "had",
    "he",
    "have",
    "her",
    "here",
    "him",
    "himself",
    "his",
    "how",
    "if",
    "in",
    "into",
    "is",
    "it",
    "its",
    "itself",
    "like",
    "make",
    "many",
    "me",
    "might",
    "more",
    "most",
    "much",
    "must",
    "my",
    "myself",
    "never",
    "now",
    "of",
    "on",
    "only",
    "or",
    "other",
    "our",
    "ours",
    "ourselves",
    "out",
    "over",
    "own",
    "said",
    "same",
    "see",
    "should",
    "since",
    "so",
    "some",
    "still",
    "such",
    "take",
    "than",
    "that",
    "the",
    "their",
    "theirs",
    "them",
    "themselves",
    "then",
    "there",
    "these",
    "they",
    "this",
    "those",
    "through",
    "to",
    "too",
    "under",
    "until",
    "up",
    "very",
    "was",
    "way",
    "we",
    "well",
    "were",
    "what",
    "where",
    "when",
    "which",
    "while",
    "who",
    "whom",
    "with",
    "would",
    "why",
    "you",
    "your",
    "yours",
    "yourself",
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
    "$",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "0",
    "_",
];

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/
class Stemmer {
    constructor() {
        this.stopwords = [...words];
    }
    stem(token) {
        return token;
    }
    addStopWord(stopWord) {
        this.stopwords.push(stopWord);
    }
    addStopWords(moreStopWords) {
        this.stopwords = this.stopwords.concat(moreStopWords);
    }
    removeStopWord(stopWord) {
        this.removeStopWords([stopWord]);
    }
    removeStopWords(moreStopWords) {
        moreStopWords.forEach(stopWord => {
            const idx = this.stopwords.indexOf(stopWord);
            if (idx >= 0) {
                this.stopwords.splice(idx, 1);
            }
        });
    }
    tokenizeAndStem(text, keepStops) {
        const stemmedTokens = [];
        const lowercaseText = text.toLowerCase();
        const tokens = new AggressiveTokenizer().tokenize(lowercaseText);
        if (keepStops) {
            tokens.forEach(token => {
                stemmedTokens.push(this.stem(token));
            });
        }
        else {
            tokens.forEach(token => {
                if (this.stopwords.indexOf(token) === -1) {
                    stemmedTokens.push(this.stem(token));
                }
            });
        }
        return stemmedTokens;
    }
}

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
const rules = {
    a: [
        {
            continuation: false,
            intact: true,
            pattern: "ia",
            size: 2,
        },
        {
            continuation: false,
            intact: true,
            pattern: "a",
            size: 1,
        },
    ],
    b: [
        {
            continuation: false,
            intact: false,
            pattern: "bb",
            size: 1,
        },
    ],
    c: [
        {
            appendage: "s",
            continuation: false,
            intact: false,
            pattern: "ytic",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ic",
            size: 2,
        },
        {
            appendage: "t",
            continuation: true,
            intact: false,
            pattern: "nc",
            size: 1,
        },
    ],
    d: [
        {
            continuation: false,
            intact: false,
            pattern: "dd",
            size: 1,
        },
        {
            appendage: "y",
            continuation: true,
            intact: false,
            pattern: "ied",
            size: 3,
        },
        {
            appendage: "ss",
            continuation: false,
            intact: false,
            pattern: "ceed",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "eed",
            size: 1,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ed",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "hood",
            size: 4,
        },
    ],
    e: [
        {
            continuation: true,
            intact: false,
            pattern: "e",
            size: 1,
        },
    ],
    f: [
        {
            appendage: "v",
            continuation: false,
            intact: false,
            pattern: "lief",
            size: 1,
        },
        {
            continuation: true,
            intact: false,
            pattern: "if",
            size: 2,
        },
    ],
    g: [
        {
            continuation: true,
            intact: false,
            pattern: "ing",
            size: 3,
        },
        {
            appendage: "y",
            continuation: false,
            intact: false,
            pattern: "iag",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ag",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "gg",
            size: 1,
        },
    ],
    h: [
        {
            continuation: false,
            intact: true,
            pattern: "th",
            size: 2,
        },
        {
            appendage: "ct",
            continuation: false,
            intact: false,
            pattern: "guish",
            size: 5,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ish",
            size: 3,
        },
    ],
    i: [
        {
            continuation: false,
            intact: true,
            pattern: "i",
            size: 1,
        },
        {
            appendage: "y",
            continuation: true,
            intact: false,
            pattern: "i",
            size: 1,
        },
    ],
    j: [
        {
            appendage: "d",
            continuation: false,
            intact: false,
            pattern: "ij",
            size: 1,
        },
        {
            appendage: "s",
            continuation: false,
            intact: false,
            pattern: "fuj",
            size: 1,
        },
        {
            appendage: "d",
            continuation: false,
            intact: false,
            pattern: "uj",
            size: 1,
        },
        {
            appendage: "d",
            continuation: false,
            intact: false,
            pattern: "oj",
            size: 1,
        },
        {
            appendage: "r",
            continuation: false,
            intact: false,
            pattern: "hej",
            size: 1,
        },
        {
            appendage: "t",
            continuation: false,
            intact: false,
            pattern: "verj",
            size: 1,
        },
        {
            appendage: "t",
            continuation: false,
            intact: false,
            pattern: "misj",
            size: 2,
        },
        {
            appendage: "d",
            continuation: false,
            intact: false,
            pattern: "nj",
            size: 1,
        },
        {
            appendage: "s",
            continuation: false,
            intact: false,
            pattern: "j",
            size: 1,
        },
    ],
    l: [
        {
            continuation: false,
            intact: false,
            pattern: "ifiabl",
            size: 6,
        },
        {
            appendage: "y",
            continuation: false,
            intact: false,
            pattern: "iabl",
            size: 4,
        },
        {
            continuation: true,
            intact: false,
            pattern: "abl",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ibl",
            size: 3,
        },
        {
            appendage: "l",
            continuation: true,
            intact: false,
            pattern: "bil",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "cl",
            size: 1,
        },
        {
            appendage: "y",
            continuation: false,
            intact: false,
            pattern: "iful",
            size: 4,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ful",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ul",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ial",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ual",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "al",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ll",
            size: 1,
        },
    ],
    m: [
        {
            continuation: false,
            intact: false,
            pattern: "ium",
            size: 3,
        },
        {
            continuation: false,
            intact: true,
            pattern: "um",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ism",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "mm",
            size: 1,
        },
    ],
    n: [
        {
            appendage: "j",
            continuation: true,
            intact: false,
            pattern: "sion",
            size: 4,
        },
        {
            appendage: "ct",
            continuation: false,
            intact: false,
            pattern: "xion",
            size: 4,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ion",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ian",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "an",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "een",
            size: 0,
        },
        {
            continuation: true,
            intact: false,
            pattern: "en",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "nn",
            size: 1,
        },
    ],
    p: [
        {
            continuation: true,
            intact: false,
            pattern: "ship",
            size: 4,
        },
        {
            continuation: false,
            intact: false,
            pattern: "pp",
            size: 1,
        },
    ],
    r: [
        {
            continuation: true,
            intact: false,
            pattern: "er",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ear",
            size: 0,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ar",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "or",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ur",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "rr",
            size: 1,
        },
        {
            continuation: true,
            intact: false,
            pattern: "tr",
            size: 1,
        },
        {
            appendage: "y",
            continuation: true,
            intact: false,
            pattern: "ier",
            size: 3,
        },
    ],
    s: [
        {
            appendage: "y",
            continuation: true,
            intact: false,
            pattern: "ies",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "sis",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "is",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ness",
            size: 4,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ss",
            size: 0,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ous",
            size: 3,
        },
        {
            continuation: false,
            intact: true,
            pattern: "us",
            size: 2,
        },
        {
            continuation: true,
            intact: true,
            pattern: "s",
            size: 1,
        },
        {
            continuation: false,
            intact: false,
            pattern: "s",
            size: 0,
        },
    ],
    t: [
        {
            appendage: "y",
            continuation: false,
            intact: false,
            pattern: "plicat",
            size: 4,
        },
        {
            continuation: true,
            intact: false,
            pattern: "at",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ment",
            size: 4,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ent",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ant",
            size: 3,
        },
        {
            appendage: "b",
            continuation: false,
            intact: false,
            pattern: "ript",
            size: 2,
        },
        {
            appendage: "b",
            continuation: false,
            intact: false,
            pattern: "orpt",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "duct",
            size: 1,
        },
        {
            continuation: false,
            intact: false,
            pattern: "sumpt",
            size: 2,
        },
        {
            appendage: "iv",
            continuation: false,
            intact: false,
            pattern: "cept",
            size: 2,
        },
        {
            appendage: "v",
            continuation: false,
            intact: false,
            pattern: "olut",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "sist",
            size: 0,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ist",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "tt",
            size: 1,
        },
    ],
    u: [
        {
            continuation: false,
            intact: false,
            pattern: "iqu",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ogu",
            size: 1,
        },
    ],
    v: [
        {
            appendage: "j",
            continuation: true,
            intact: false,
            pattern: "siv",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "eiv",
            size: 0,
        },
        {
            continuation: true,
            intact: false,
            pattern: "iv",
            size: 2,
        },
    ],
    y: [
        {
            continuation: true,
            intact: false,
            pattern: "bly",
            size: 1,
        },
        {
            appendage: "y",
            continuation: true,
            intact: false,
            pattern: "ily",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ply",
            size: 0,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ly",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ogy",
            size: 1,
        },
        {
            continuation: false,
            intact: false,
            pattern: "phy",
            size: 1,
        },
        {
            continuation: false,
            intact: false,
            pattern: "omy",
            size: 1,
        },
        {
            continuation: false,
            intact: false,
            pattern: "opy",
            size: 1,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ity",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ety",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "lty",
            size: 2,
        },
        {
            continuation: false,
            intact: false,
            pattern: "istry",
            size: 5,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ary",
            size: 3,
        },
        {
            continuation: true,
            intact: false,
            pattern: "ory",
            size: 3,
        },
        {
            continuation: false,
            intact: false,
            pattern: "ify",
            size: 3,
        },
        {
            appendage: "t",
            continuation: true,
            intact: false,
            pattern: "ncy",
            size: 2,
        },
        {
            continuation: true,
            intact: false,
            pattern: "acy",
            size: 3,
        },
    ],
    z: [
        {
            continuation: true,
            intact: false,
            pattern: "iz",
            size: 2,
        },
        {
            appendage: "s",
            continuation: false,
            intact: false,
            pattern: "yz",
            size: 1,
        },
    ],
};

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
class LancasterStemmer extends Stemmer {
    constructor() {
        super();
    }
    // take a token, look up the applicatble rule section and attempt some stemming!
    applyRuleSection(token, intact) {
        const section = token.substr(-1);
        const rules$1 = rules[section];
        if (rules$1) {
            for (let i = 0; i < rules$1.length; i++) {
                if ((intact || !rules$1[i].intact) &&
                    // only apply intact rules to intact tokens
                    token.substr(0 - rules$1[i].pattern.length) ===
                        rules$1[i].pattern) {
                    // hack off only as much as the rule indicates
                    let result = token.substr(0, token.length - rules$1[i].size);
                    // if the rules wants us to apply an appendage do so
                    if (rules$1[i].appendage) {
                        result += rules$1[i].appendage;
                    }
                    if (this.acceptable(result)) {
                        token = result;
                        // see what the rules wants to do next
                        if (rules$1[i].continuation) {
                            // this rule thinks there still might be stem left. keep at it.
                            // since we've applied a change we'll pass false in for intact
                            return this.applyRuleSection(result, false);
                        }
                        else {
                            // the rule thinks we're done stemming. drop out.
                            return result;
                        }
                    }
                }
            }
        }
        return token;
    }
    acceptable(candidate) {
        if (candidate.match(/^[aeiou]/)) {
            return candidate.length > 1;
        }
        else {
            return candidate.length > 2 && candidate.match(/[aeiouy]/);
        }
    }
    stem(token) {
        return this.applyRuleSection(token.toLowerCase(), true);
    }
}

/*
Copyright (c) 2011, Chris Umbel
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
// denote groups of consecutive consonants with a C and consecutive vowels
// with a V.
function categorizeGroups(token) {
    return token
        .replace(/[^aeiouy]+y/g, "CV")
        .replace(/[aeiou]+/g, "V")
        .replace(/[^V]+/g, "C");
}
// denote single consonants with a C and single vowels with a V
function categorizeChars(token) {
    return token
        .replace(/[^aeiouy]y/g, "CV")
        .replace(/[aeiou]/g, "V")
        .replace(/[^V]/g, "C");
}
// calculate the "measure" M of a word. M is the count of VC sequences dropping
// an initial C if it exists and a trailing V if it exists.
function measure(token) {
    if (!token) {
        return -1;
    }
    return (categorizeGroups(token).replace(/^C/, "").replace(/V$/, "").length / 2);
}
// determine if a token end with a double consonant i.e. happ
function endsWithDoublCons(token) {
    return token.match(/([^aeiou])\1$/);
}
// replace a pattern in a word. if a replacement occurs an optional callback
// can be called to post-process the result. if no match is made NULL is
// returned.
function attemptReplace(token, pattern, replacement, callback) {
    let result = null;
    if (typeof pattern === "string" &&
        token.substr(0 - pattern.length) === pattern) {
        result = token.replace(new RegExp(pattern + "$"), replacement);
    }
    else if (pattern instanceof RegExp && token.match(pattern)) {
        result = token.replace(pattern, replacement);
    }
    if (result && callback) {
        return callback(result);
    }
    else {
        return result;
    }
}
// attempt to replace a list of patterns/replacements on a token for a minimum
// measure M.
function attemptReplacePatterns(token, replacements, measureThreshold = null) {
    let replacement = token;
    for (let i = 0; i < replacements.length; i++) {
        if (measureThreshold == null ||
            measure(attemptReplace(token, replacements[i][0], replacements[i][1])) > measureThreshold) {
            replacement =
                attemptReplace(replacement, replacements[i][0], replacements[i][2]) || replacement;
        }
    }
    return replacement;
}
// replace a list of patterns/replacements on a word. if no match is made return
// the original token.
function replacePatterns(token, replacements, measureThreshold) {
    return (attemptReplacePatterns(token, replacements, measureThreshold) || token);
}
// TODO: this should replace all of the messy replacement stuff above
function replaceRegex(token, regex, includeParts, minimumMeasure) {
    let result = "";
    if (regex.test(token)) {
        const parts = regex.exec(token);
        if (parts !== null) {
            includeParts.forEach(i => {
                result += parts[i];
            });
        }
    }
    if (measure(result) > minimumMeasure) {
        return result;
    }
    return null;
}
// step 1a as defined for the porter stemmer algorithm.
function step1a(token) {
    if (token.match(/(ss|i)es$/)) {
        return token.replace(/(ss|i)es$/, "$1");
    }
    if (token.substr(-1) === "s" &&
        token.substr(-2, 1) !== "s" &&
        token.length > 2) {
        return token.replace(/s?$/, "");
    }
    return token;
}
// step 1b as defined for the porter stemmer algorithm.
function step1b(token) {
    let result;
    if (token.substr(-3) === "eed") {
        if (measure(token.substr(0, token.length - 3)) > 0) {
            return token.replace(/eed$/, "ee");
        }
    }
    else {
        result = attemptReplace(token, /(ed|ing)$/, "", (token) => {
            if (categorizeGroups(token).indexOf("V") >= 0) {
                result = attemptReplacePatterns(token, [
                    ["at", "", "ate"],
                    ["bl", "", "ble"],
                    ["iz", "", "ize"],
                ]);
                if (result !== token) {
                    return result;
                }
                else {
                    if (endsWithDoublCons(token) && token.match(/[^lsz]$/)) {
                        return token.replace(/([^aeiou])\1$/, "$1");
                    }
                    if (measure(token) === 1 &&
                        categorizeChars(token).substr(-3) === "CVC" &&
                        token.match(/[^wxy]$/)) {
                        return token + "e";
                    }
                }
                return token;
            }
            return null;
        });
        if (result) {
            return result;
        }
    }
    return token;
}
// step 1c as defined for the porter stemmer algorithm.
function step1c(token) {
    const categorizedGroups = categorizeGroups(token);
    if (token.substr(-1) === "y" &&
        categorizedGroups.substr(0, categorizedGroups.length - 1).indexOf("V") >
            -1) {
        return token.replace(/y$/, "i");
    }
    return token;
}
// step 2 as defined for the porter stemmer algorithm.
function step2(token) {
    token = replacePatterns(token, [
        ["ational", "", "ate"],
        ["tional", "", "tion"],
        ["enci", "", "ence"],
        ["anci", "", "ance"],
        ["izer", "", "ize"],
        ["abli", "", "able"],
        ["bli", "", "ble"],
        ["alli", "", "al"],
        ["entli", "", "ent"],
        ["eli", "", "e"],
        ["ousli", "", "ous"],
        ["ization", "", "ize"],
        ["ation", "", "ate"],
        ["ator", "", "ate"],
        ["alism", "", "al"],
        ["iveness", "", "ive"],
        ["fulness", "", "ful"],
        ["ousness", "", "ous"],
        ["aliti", "", "al"],
        ["iviti", "", "ive"],
        ["biliti", "", "ble"],
        ["logi", "", "log"],
    ], 0);
    return token;
}
// step 3 as defined for the porter stemmer algorithm.
function step3(token) {
    return replacePatterns(token, [
        ["icate", "", "ic"],
        ["ative", "", ""],
        ["alize", "", "al"],
        ["iciti", "", "ic"],
        ["ical", "", "ic"],
        ["ful", "", ""],
        ["ness", "", ""],
    ], 0);
}
// step 4 as defined for the porter stemmer algorithm.
function step4(token) {
    return (replaceRegex(token, /^(.+?)(al|ance|ence|er|ic|able|ible|ant|ement|ment|ent|ou|ism|ate|iti|ous|ive|ize)$/, [1], 1) ||
        replaceRegex(token, /^(.+?)([st])(ion)$/, [1, 2], 1) ||
        token);
}
// step 5a as defined for the porter stemmer algorithm.
function step5a(token) {
    const m = measure(token.replace(/e$/, ""));
    if (m > 1 ||
        (m === 1 &&
            !(categorizeChars(token).substr(-4, 3) === "CVC" &&
                token.match(/[^wxy].$/)))) {
        token = token.replace(/e$/, "");
    }
    return token;
}
// step 5b as defined for the porter stemmer algorithm.
function step5b(token) {
    if (measure(token) > 1) {
        return token.replace(/ll$/, "l");
    }
    return token;
}
// perform full stemming algorithm on a single word
class PorterStemmer extends Stemmer {
    stem(token) {
        if (token.length < 3)
            return token.toString();
        return step5b(step5a(step4(step3(step2(step1c(step1b(step1a(token.toLowerCase())))))))).toString();
    }
}

const getStemmer = (stemmer = "identity") => {
    switch (stemmer) {
        case "porter":
            return new PorterStemmer();
        case "lancaster":
            return new LancasterStemmer();
        default:
        case "identity":
            return new Stemmer();
    }
};

/**
 * Constants
 */
const defaultStopwords = ["a", "a's", "able", "about", "above", "according", "accordingly", "across", "actually", "after", "afterwards", "again", "against", "ain't", "all", "allow", "allows", "almost", "alone", "along", "already", "also", "although", "always", "am", "among", "amongst", "an", "and", "another", "any", "anybody", "anyhow", "anyone", "anything", "anyway", "anyways", "anywhere", "apart", "appear", "appreciate", "appropriate", "are", "aren't", "around", "as", "aside", "ask", "asking", "associated", "at", "available", "away", "awfully", "b", "be", "became", "because", "become", "becomes", "becoming", "been", "before", "beforehand", "behind", "being", "believe", "below", "beside", "besides", "best", "better", "between", "beyond", "both", "brief", "but", "by", "c", "c'mon", "c's", "came", "can", "can't", "cannot", "cant", "cause", "causes", "certain", "certainly", "changes", "clearly", "co", "com", "come", "comes", "concerning", "consequently", "consider", "considering", "contain", "containing", "contains", "corresponding", "could", "couldn't", "course", "currently", "d", "definitely", "described", "despite", "did", "didn't", "different", "do", "does", "doesn't", "doing", "don't", "done", "down", "downwards", "during", "e", "each", "edu", "eg", "eight", "either", "else", "elsewhere", "enough", "entirely", "especially", "et", "etc", "even", "ever", "every", "everybody", "everyone", "everything", "everywhere", "ex", "exactly", "example", "except", "f", "far", "few", "fifth", "first", "five", "followed", "following", "follows", "for", "former", "formerly", "forth", "four", "from", "further", "furthermore", "g", "get", "gets", "getting", "given", "gives", "go", "goes", "going", "gone", "got", "gotten", "greetings", "h", "had", "hadn't", "happens", "hardly", "has", "hasn't", "have", "haven't", "having", "he", "he's", "hello", "help", "hence", "her", "here", "here's", "hereafter", "hereby", "herein", "hereupon", "hers", "herself", "hi", "him", "himself", "his", "hither", "hopefully", "how", "howbeit", "however", "i", "i'd", "i'll", "i'm", "i've", "ie", "if", "ignored", "immediate", "in", "inasmuch", "inc", "indeed", "indicate", "indicated", "indicates", "inner", "insofar", "instead", "into", "inward", "is", "isn't", "it", "it'd", "it'll", "it's", "its", "itself", "j", "just", "k", "keep", "keeps", "kept", "know", "known", "knows", "l", "last", "lately", "later", "latter", "latterly", "least", "less", "lest", "let", "let's", "like", "liked", "likely", "little", "look", "looking", "looks", "ltd", "m", "mainly", "many", "may", "maybe", "me", "mean", "meanwhile", "merely", "might", "more", "moreover", "most", "mostly", "much", "must", "my", "myself", "n", "name", "namely", "nd", "near", "nearly", "necessary", "need", "needs", "neither", "never", "nevertheless", "new", "next", "nine", "no", "nobody", "non", "none", "noone", "nor", "normally", "not", "nothing", "novel", "now", "nowhere", "o", "obviously", "of", "off", "often", "oh", "ok", "okay", "old", "on", "once", "one", "ones", "only", "onto", "or", "other", "others", "otherwise", "ought", "our", "ours", "ourselves", "out", "outside", "over", "overall", "own", "p", "particular", "particularly", "per", "perhaps", "placed", "please", "plus", "possible", "presumably", "probably", "provides", "q", "que", "quite", "qv", "r", "rather", "rd", "re", "really", "reasonably", "regarding", "regardless", "regards", "relatively", "respectively", "right", "s", "said", "same", "saw", "say", "saying", "says", "second", "secondly", "see", "seeing", "seem", "seemed", "seeming", "seems", "seen", "self", "selves", "sensible", "sent", "serious", "seriously", "seven", "several", "shall", "she", "should", "shouldn't", "since", "six", "so", "some", "somebody", "somehow", "someone", "something", "sometime", "sometimes", "somewhat", "somewhere", "soon", "sorry", "specified", "specify", "specifying", "still", "sub", "such", "sup", "sure", "t", "t's", "take", "taken", "tell", "tends", "th", "than", "thank", "thanks", "thanx", "that", "that's", "thats", "the", "their", "theirs", "them", "themselves", "then", "thence", "there", "there's", "thereafter", "thereby", "therefore", "therein", "theres", "thereupon", "these", "they", "they'd", "they'll", "they're", "they've", "think", "third", "this", "thorough", "thoroughly", "those", "though", "three", "through", "throughout", "thru", "thus", "to", "together", "too", "took", "toward", "towards", "tried", "tries", "truly", "try", "trying", "twice", "two", "u", "un", "under", "unfortunately", "unless", "unlikely", "until", "unto", "up", "upon", "us", "use", "used", "useful", "uses", "using", "usually", "uucp", "v", "value", "various", "very", "via", "viz", "vs", "w", "want", "wants", "was", "wasn't", "way", "we", "we'd", "we'll", "we're", "we've", "welcome", "well", "went", "were", "weren't", "what", "what's", "whatever", "when", "whence", "whenever", "where", "where's", "whereafter", "whereas", "whereby", "wherein", "whereupon", "wherever", "whether", "which", "while", "whither", "who", "who's", "whoever", "whole", "whom", "whose", "why", "will", "willing", "wish", "with", "within", "without", "won't", "wonder", "would", "wouldn't", "x", "y", "yes", "yet", "you", "you'd", "you'll", "you're", "you've", "your", "yours", "yourself", "yourselves", "z", "zero"]; // prettier-ignore
/**
 * Sorts a word array by word frequency.
 */
function sortedFreqWords(words) {
    const frequency = {};
    words.forEach(word => {
        if (frequency[word.text]) {
            frequency[word.text].value += word.value;
        }
        else {
            frequency[word.text] = {
                text: word.text,
                value: word.value,
            };
        }
    });
    return Object.values(frequency).sort((a, b) => b.value - a.value);
}
/**
 * Creates an array of words from an array of string, sorts by frequency.
 */
function sortedFrequency(tokens) {
    const frequency = {};
    tokens.forEach(token => {
        if (frequency[token]) {
            frequency[token].value += 1;
        }
        else {
            frequency[token] = {
                text: token,
                value: 1,
            };
        }
    });
    return Object.values(frequency).sort((a, b) => b.value - a.value);
}
/**
 * creates a word array from an input string
 */
function tokenizeWords(content, settings) {
    const { allowNumbers = true, maxWords, stemmer = "identity", stopwordsInput = "", } = settings;
    const stopwords = stopwordsInput
        .split(",")
        .map(stopword => stopword.toLowerCase().trim());
    const stopwordsSet = new Set([...defaultStopwords, ...stopwords].map(stopword => stopword.toLowerCase()));
    const matched = content.match(/\w+/g);
    if (!matched) {
        return [];
    }
    const stemmerImpl = getStemmer(stemmer);
    const tokens = matched
        .map(token => token.toLowerCase())
        .filter(token => {
        const filterByStopwords = !stopwordsSet.has(token);
        return allowNumbers
            ? filterByStopwords
            : filterByStopwords && Number.isNaN(Number(token));
    })
        .map(token => {
        return stemmerImpl.stem(token);
    });
    const f = sortedFrequency(tokens);
    if (maxWords !== undefined) {
        return f.slice(0, maxWords);
    }
    else {
        return f;
    }
}
/**
 * Splits word array into individual words and then sorts by frequency
 */
function normalizeWords(data, settings) {
    const r = data.map(sentence => {
        const words = [];
        for (const word of tokenizeWords(sentence.text, settings)) {
            words.push({
                text: word.text,
                value: word.value * sentence.value,
            });
        }
        return words;
    });
    const flat = r.flat();
    const result = sortedFreqWords(flat);
    return result;
}
/**
 * Creates a sorted word array from either an input text or already tokenized input data
 */
function deriveValues(text, data, settings = {
    allowNumbers: false,
    stemmer: "identity",
    stopwordsInput: "",
}) {
    if (text && data) {
        console.warn("Supplied text AND data. Using data.");
    }
    if (data) {
        const words = normalizeWords(data, settings).sort((x, y) => y.value - x.value);
        return words;
    }
    else if (text) {
        return tokenizeWords(text, settings).sort((x, y) => y.value - x.value);
    }
    return [];
}

export { WordCloudFixed as WordCloud, WordCloudResponsive, deriveValues };
//# sourceMappingURL=index.mjs.map
